import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { format } from 'date-fns';
import { EstimateResponse } from '../../types/estimate';
import { formatCurrency } from '../../utils/formatters';
import DocumentAuditHistory from '../audit/DocumentAuditHistory';

interface EstimateContentProps {
  estimate: EstimateResponse;
}

const EstimateContent: React.FC<EstimateContentProps> = ({ estimate }) => {
  return (
    <Grid container spacing={3}>
      {/* Estimate Information */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Estimate Details
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Estimate Date
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(estimate.estimate_date), 'dd/MM/yyyy')}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Valid Until
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(estimate.valid_until), 'dd/MM/yyyy')}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Box mt={0.5}>
                      <Chip 
                        label={estimate.status.toUpperCase()} 
                        size="small"
                        color={estimate.status === 'accepted' ? 'success' : 
                               estimate.status === 'rejected' ? 'error' :
                               estimate.status === 'expired' ? 'warning' :
                               estimate.status === 'converted' ? 'secondary' : 'default'}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Customer
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {estimate.customer_name || 'N/A'}
                    </Typography>
                    {estimate.customer_email && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {estimate.customer_email}
                      </Typography>
                    )}
                    {estimate.customer_mobile && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {estimate.customer_mobile}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Billing Address
                    </Typography>
                    {estimate.billing_address ? (
                      <Box>
                        <Typography variant="body2">
                          {estimate.billing_address.address_line1}
                          {estimate.billing_address.address_line2 && `, ${estimate.billing_address.address_line2}`}
                        </Typography>
                        <Typography variant="body2">
                          {estimate.billing_address.city}, {estimate.billing_address.state} {estimate.billing_address.postal_code}
                        </Typography>
                        {estimate.billing_address.contact_person && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Contact: {estimate.billing_address.contact_person}
                            {estimate.billing_address.contact_phone && ` - ${estimate.billing_address.contact_phone}`}
                          </Typography>
                        )}
                        {estimate.billing_address.gstin && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            GSTIN: {estimate.billing_address.gstin}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Not specified</Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Shipping Address
                    </Typography>
                    {estimate.shipping_address ? (
                      <Box>
                        <Typography variant="body2">
                          {estimate.shipping_address.address_line1}
                          {estimate.shipping_address.address_line2 && `, ${estimate.shipping_address.address_line2}`}
                        </Typography>
                        <Typography variant="body2">
                          {estimate.shipping_address.city}, {estimate.shipping_address.state} {estimate.shipping_address.postal_code}
                        </Typography>
                        {estimate.shipping_address.contact_person && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Contact: {estimate.shipping_address.contact_person}
                            {estimate.shipping_address.contact_phone && ` - ${estimate.shipping_address.contact_phone}`}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Same as billing address</Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {/* Conversion Status */}
              {estimate.converted_to_order_id && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="success.dark" gutterBottom>
                      Conversion Status
                    </Typography>
                    <Typography variant="body2" color="success.dark">
                      This estimate has been converted to Sales Order: {estimate.converted_to_order_id.substring(0, 8)}...
                      {estimate.converted_at && (
                        <> on {format(new Date(estimate.converted_at), 'dd/MM/yyyy HH:mm')}</>
                      )}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {estimate.notes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {estimate.notes}
                  </Typography>
                </Grid>
              )}
              
              {estimate.terms_and_conditions && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Terms and Conditions
                  </Typography>
                  <Typography variant="body1">
                    {estimate.terms_and_conditions}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Financial Summary */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Financial Summary
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(estimate.subtotal)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="h6" color="error">
                    -{formatCurrency(estimate.discount_amount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Discount
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2} bgcolor="grey.50" borderRadius={1}>
                  <Typography variant="h6" color="warning.main">
                    {formatCurrency(estimate.tax_amount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tax
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2} bgcolor="primary.light" borderRadius={1}>
                  <Typography variant="h6" color="primary.dark">
                    {formatCurrency(estimate.total_amount)}
                  </Typography>
                  <Typography variant="body2" color="primary.dark">
                    Total Amount
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Line Items */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Line Items ({estimate.items.length} items)
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Tax</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {estimate.items.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.product_name || 'Product'}
                            {item.variant_name && item.variant_name !== 'Default' && ` - ${item.variant_name}`}
                          </Typography>
                          {item.item_code && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Item Code: {item.item_code}
                            </Typography>
                          )}
                          {item.description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.description}
                            </Typography>
                          )}
                          {item.hsn_code && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              HSN: {item.hsn_code}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(item.unit_price)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.discount_percent}%
                          {item.discount_amount && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              ({formatCurrency(item.discount_amount)})
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.tax_rate && `${item.tax_rate}%`}
                          {item.tax_amount && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              ({formatCurrency(item.tax_amount)})
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(item.line_total || (item.quantity * item.unit_price))}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Audit History */}
      <Grid item xs={12}>
        <DocumentAuditHistory
          documentId={estimate.id}
          documentType="estimate"
          title="Estimate Audit History"
        />
      </Grid>
    </Grid>
  );
};

export default EstimateContent;